import { CdkScrollable } from "@angular/cdk/scrolling";
import { CommonModule, CurrencyPipe, DatePipe } from "@angular/common";
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatOptionModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatSelectChange, MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTooltipModule } from "@angular/material/tooltip";
import { FuseConfirmationService } from "@fuse/services/confirmation";
import { BomDto, BomItemDto, DlmProductDto, DlmProductFileDto, EnumViewModel, ManufactureProcessCategoryBreakdownDto, ManufactureProcessColumnDefinitionDto, ManufactureProcessDto, ManufactureProcessStageDto, ManufactureProcessStepDto, PartResponse, ProductionResourceDto, PurchaseOrderDto, PurchaseOrderPartItemDto, SalesOrderLineItemPriority, SupplierDto, UserDto, VatOption, WorkOrderComponentStatus, WorkOrderDto, WorkOrderProcessStageInfoDto, WorkOrderStageInfoDto, WorkOrderStatus, WorkOrderSwimlane } from "app/api/models";
import { BomService, DlmProductService, EmployeeService, ManufactureProcessService, PartService, PurchaseOrderService, SupplierService, WorkOrderService } from "app/api/services";
import { AppAuthService } from "app/api/services/cookie-jwt-auth/app-auth.service";
import { SharedModule } from "app/core/shared.module";
import { TimeUtil } from "app/core/utils/time-utils";
import { isNumber } from "lodash";
import { NgxPrintModule } from "ngx-print";
import { PurchaseOrderBuilderComponent } from "../../../purchase-orders/purchase-order-builder/purchase-order-builder.component";
import { BomDetailsPopupComponent } from "../../../product-register/boms/bom-details-popup/bom-details-popup.component";
import { interval, Subscription } from "rxjs";
import { AddItemDialogComponent } from "app/core/components/add-item-dialog/add-item-dialog.component";
import { sortOnProperty, sumList } from "app/app.component";
import { GenericInfoDialogService } from "app/core/services/generic-info-dialog/infoDialog.service";
import { WorkOrderUtils } from "../../WorkOrderUtils";
import { QRCodeModule } from "angularx-qrcode";
import { UploadFilePopupComponent } from "app/views/admin/installation/documentations/upload-file-popup/upload-file-popup.component";

@Component({
    selector       : 'work-order-dialog',
    templateUrl    : './work-order-dialog.component.html',
    styleUrls      : ['./work-order-dialog.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        SharedModule,
        MatButtonModule,
        CdkScrollable,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatOptionModule,
        FormsModule,
        ReactiveFormsModule,
        CurrencyPipe,
        MatDatepickerModule,
        MatAutocompleteModule,
        NgxPrintModule,
        MatSlideToggleModule,
        MatTooltipModule,
        MatCheckboxModule,
        MatProgressBarModule,
        MatExpansionModule,
        QRCodeModule,
    ],
})
export class WorkOrderDialogComponent implements OnInit, OnDestroy
{
    public workOrderId: number;
    public workOrder: WorkOrderDto;
    private _originalDueDate: string = null;
    public form: FormGroup;
    public files: DlmProductFileDto[] = [];
    
    public currentStageInfo: WorkOrderStageInfoDto;
    public bom: BomDto;
    public salesOrders: PurchaseOrderDto[] = [];

    public allUsers: UserDto[] = [];
    public parts: PartResponse[] = [];
    public products: DlmProductDto[] = [];
    public machines: ProductionResourceDto[] = [];
    public suppliers: SupplierDto[] = [];

    public manufactureProcesses : ManufactureProcessCategoryBreakdownDto[] = [];

    salesOrderPriorities: EnumViewModel[] = Object.values(SalesOrderLineItemPriority).filter(x => isNumber(x)).map(x => <EnumViewModel> {name: this.getPriorityName(+x), value: <SalesOrderLineItemPriority>x});
    statuses: EnumViewModel[] = Object.values(WorkOrderStatus).filter(x => isNumber(x)).map(x => <EnumViewModel> {name: this.getStatusName(+x), value: <WorkOrderStatus>x});
    
    public couriers = [
        'Royal Mail',
        'Evri',
        'DPD',
        'DPD Local',
        'UPS',
        'DHL',
        'DHL Express',
        'Parcelforce Worldwide',
        'Yodel',
        'FedEx',
        'DX',
        'Amazon Logistics',
        'TNT',
        'UK Mail',
        'Hermes (legacy)',
        'CitySprint',
        'APC Overnight',
        'Tuffnells',
        'Palletforce',
        'Pall-Ex'
    ];

    private timerSub: Subscription;
    public timerSeconds: number = 0;
    public stageTimerSeconds: number = 0;
    public timeOn: boolean = false;
    public currentStageAssignedUserId: number | null = null;
    
    public process: ManufactureProcessDto;
    public selectedStageId: number;

    public WorkOrderSwimlane = WorkOrderSwimlane;

    private isSuperAdmin = false;

    constructor(
        public dialogRef: MatDialogRef<WorkOrderDialogComponent>,
        private partService: PartService,
        private purchaseOrderService: PurchaseOrderService,
        private dlmProductService: DlmProductService,
        private workOrderService: WorkOrderService,
        private employeeService: EmployeeService,
        private bomService: BomService,
        private manufactureProcessService: ManufactureProcessService,
        private supplierService: SupplierService,
        private _formBuilder: FormBuilder,
        private cdr: ChangeDetectorRef,
        private datePipe: DatePipe,
        private dialog: MatDialog,
        private _fuseConfirmationService: FuseConfirmationService,
        public _infoDialogService: GenericInfoDialogService,
        private appAuth: AppAuthService,
        @Inject(MAT_DIALOG_DATA) public data: {workOrderId: number}
    )
    {
    }

    ngOnInit(): void
    {
        this.workOrderId = this.data.workOrderId ?? null;
        this.isSuperAdmin = this.appAuth.isSuperAdmin();

        this.loadParts();
        this.loadProducts();
        this.loadUsers();
        this.loadMachines();
        this.loadSuppliers();

        this.loadWorkOrder();
    }
    
    ngOnDestroy() {
        this.stopTimer();
    }

    loadWorkOrder() {
        if(!this.workOrderId) {
            this.loadSalesOrders();
            this.buildForm();
            return;
        }

        this.workOrderService.apiWorkOrderGetWorkOrderPost$Json$Response({id: this.workOrderId}).subscribe(res => {
            if(!res.body.data) return;

            this.workOrder = res.body.data;
            this._originalDueDate = this.workOrder?.dueDate ?? null;
            console.log("work order", this.workOrder);

            // Sort history and comments newest first
            this.workOrder.history = sortOnProperty(this.workOrder.history ?? [], 'timeStamp', false);
            this.workOrder.workOrderComments = sortOnProperty(this.workOrder.workOrderComments ?? [], 'timeStamp', false);

            this.currentStageInfo = this.workOrder.stageInfos?.find(x =>
                x.resourceSwimLaneId == this.workOrder.resourceSwimLaneId && x.workOrderSwimlane == this.workOrder.workOrderSwimlane) ?? null;

            this.stageTimerSeconds = this.currentStageInfo?.timerSeconds ?? 0;
            this.currentStageAssignedUserId = this.currentStageInfo?.assignedUserId ?? null;

            this.loadBomForProduct();
            this.loadFilesForProduct();

            // if(this.hasReachedGauging())
            this.loadManufactureProcesses();

            this.buildForm();
        });
    }

    loadSalesOrders() {
        this.purchaseOrderService.apiPurchaseOrderGetAllGet$Json$Response({isSupplier: false}).subscribe(res => {
            if(!res?.body?.data) return;
            this.salesOrders = res.body.data.filter(x => x.isProduct);
            console.log("sos", this.salesOrders);
        });
    }

    loadBomForProduct() {
        this.bomService.apiBomBomByProductIdGet$Json$Response({productId: this.workOrder.productId}).subscribe(res => {
            if(!res?.body?.data) return;
            this.bom = res.body.data;

            for(var item of this.bom.items)
                if(!this.getComponentInfoForBomItem(item))
                    this.workOrder.componentInfos.push({
                        bomItemId: item.bomItemId,
                        partId: item.partId,
                        status: WorkOrderComponentStatus.NotReceived,
                        workOrderId: this.workOrderId,
                    });
        });
    }
    
    loadUsers() {
        this.employeeService.apiEmployeeSearchEmployeeUsersGet$Json$Response({archived: false}).subscribe(res => {
            if(!res?.body.data) return;
            this.allUsers = res.body.data;
        });
    }

    loadProducts() {
        this.dlmProductService.apiDlmProductGetProductsGet$Json$Response().subscribe((res) => {
            if (!res?.body?.data) return;

            this.products = res.body.data;
        });
    }

    loadParts() {
        this.partService.apiPartPartsGet$Json$Response().subscribe(res => {
            if(!res?.body?.data) return;
            this.parts = res.body.data;
        });
    }

    loadMachines() {
        this.workOrderService.apiWorkOrderGetResourcesGet$Json$Response().subscribe((res) => {
            if (!res?.body?.data) return;
            this.machines = res.body.data.filter(x => x.showSwimlane);
        });
    }

    loadManufactureProcesses() {
        this.manufactureProcessService.apiManufactureProcessGetCategoriesBreakdownGet$Json$Response().subscribe(res => {
            if(!res?.body?.data) return;
            this.manufactureProcesses = res.body.data;

            if(this.hasReachedGauging() && this.workOrder?.manufactureColumnDefinitionId)
                this.loadChosenManufactureProcess(this.workOrder.manufactureColumnDefinitionId);
        });
    }

    loadSuppliers() {
        this.supplierService.apiSupplierGetAllGet$Json().subscribe(res => {
            if(!res?.data) return;

            this.suppliers = res.data;
        });
    }
    
    buildForm() {
        this.form = this._formBuilder.group({
            startDate           : [this.workOrder?.startDate ?? new Date(),                             Validators.required],
            dueDate             : [this.workOrder?.revisedDueDate ?? this.workOrder?.dueDate ?? null,    Validators.required],
            expectedArrivalDate : [this.workOrder?.expectedArrivalDate ?? null],
            assignedUserId      : [null],
            priority            : [this.workOrder?.priority ?? SalesOrderLineItemPriority.Normal,       Validators.required],
            status              : [this.workOrder?.status ?? WorkOrderStatus.Created,                   Validators.required],
            quantity            : [this.workOrder?.quantity ?? 1,                                       [Validators.required, Validators.min(1)]],
            supplierId          : [this.workOrder?.supplierId ?? null],

            actualStartDate     : [this.workOrder?.actualStartDate ?? null],
            actualFinishDate    : [this.workOrder?.actualFinishDate ?? null],
            comments            : [this.workOrder?.comments ?? ''],

            productId           : [this.workOrder?.productId ?? null,                                   Validators.required],
            salesOrderId        : [this.workOrder?.salesOrderId ?? null,                                Validators.required],

            assignedMachineId   : [this.workOrder?.assignedMachineId ?? null],

            manufactureColumnDefinitionId : [this.workOrder?.manufactureColumnDefinitionId ?? null],

            //dispatch
            courier             : [this.workOrder?.courier ?? ''],
            trackingNumber      : [this.workOrder?.trackingNumber ?? ''],
            numberOfPackages    : [this.workOrder?.numberOfPackages ?? 1],
            weightKg            : [this.workOrder?.weightKg ?? 0],
            dispatchDate        : [this.workOrder?.dispatchDate ?? null],
            revisedDispatchDate : [this.workOrder?.revisedDispatchDate ?? null],
            reasonForDateChange : [this.workOrder?.reasonForDateChange ?? null],
            dispatchNotes       : [this.workOrder?.dispatchNotes ?? ''],
        });

        if(!this.isSuperAdmin)
            this.form.controls['dispatchDate'].disable();

        this.cdr.detectChanges();
    }

    startTimer() {
        this.timeOn = true;
        this.timerSub = interval(1000).subscribe(() => {
            this.timerSeconds++;
        });
    }
    stopTimer() {
        this.timeOn = false;
        this.timerSub?.unsubscribe();
    }
    getFormattedSeconds(totalSeconds) {
        var total = totalSeconds;

        var hours = Math.floor(total / 60 / 60);
        total -= hours * 60 * 60;

        var mins = Math.floor(total / 60);
        total -= mins * 60;

        var seconds = total;

        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    getSwimlaneNameForStage(stage: WorkOrderStageInfoDto) {
        if(stage.resourceSwimLaneId)
            return this.machines?.find(x => x.productionResourceId == stage.resourceSwimLaneId)?.name;
        else
            return WorkOrderUtils.getSwimlaneName(stage.workOrderSwimlane);
    }

    getProductsForSelectedSalesOrder() {
        var purchaseOrderId = this.form.controls['salesOrderId'].value;
        if(!purchaseOrderId) return [];

        var items = this.salesOrders.find(x => x.purchaseOrderId == purchaseOrderId)?.purchaseOrderProductItems ?? [];

        return items.map(x => this.lookupProductById(x.productId)).filter(x => !!x);
    }

    getComponentInfoForBomItem = (bomItem: BomItemDto) => this.workOrder?.componentInfos?.find(x => x.bomItemId == bomItem.bomItemId);
    
    isComponentReceived = (bomItem: BomItemDto) => this.getComponentInfoForBomItem(bomItem)?.status >= WorkOrderComponentStatus.Received;
    isComponentInspected = (bomItem: BomItemDto) => [WorkOrderComponentStatus.Failed, WorkOrderComponentStatus.Passed].includes(this.getComponentInfoForBomItem(bomItem)?.status);

    hasReachedGoodsIn = () => this.workOrder?.workOrderSwimlane != null && this.workOrder?.workOrderSwimlane >= +WorkOrderSwimlane.GoodsIn;
    hasReachedGauging = () => this.workOrder?.workOrderSwimlane != null && this.workOrder?.workOrderSwimlane >= +WorkOrderSwimlane.Gauging;

    lookupProductById(productId: number) {
        if(!productId)
            return null;

        return this.products?.find(x => x.dlmProductId == productId) ?? null;
    }
    lookupPartById(partId: number) {
        if(!partId)
            return null;

        return this.parts?.find(x => x.id == partId) ?? null;
    }

    getBomItemRequiredAmount = (item: BomItemDto) => item.quantity * this.workOrder.quantity;
    getPartCurrentStock = (partId: number) => this.lookupPartById(partId)?.currentStock ?? 0;

    isOnTime = () => this.workOrder.actualStartDate <= this.workOrder.startDate;

    getPriorityName(priority: number) {
        switch(priority) {
            default: return SalesOrderLineItemPriority[priority].toString();
        }
    }

    getStatusName(status: number) {
        switch(status) {
            case WorkOrderStatus.InProgress: return 'In Progress';
            case WorkOrderStatus.QaHold: return 'QA Hold';
            default: return WorkOrderStatus[status].toString();
        }
    }

    getComponentStatusName(status: number) {
        switch(status) {
            case WorkOrderComponentStatus.NotReceived: return 'Pending';
            default: return WorkOrderComponentStatus[status].toString();
        }
    }

    getComponentStatusClasses(status: number) {
        var classStr = 'uppercase text-sm px-2 py-1 ';
        
        switch(status) {
            case WorkOrderComponentStatus.NotReceived: return classStr + 'bg-yellow-100 text-yellow-600';
            case WorkOrderComponentStatus.Received: return classStr + 'bg-blue-100 text-blue-600';
            case WorkOrderComponentStatus.Failed: return classStr + 'bg-red-100 text-red-600';
            case WorkOrderComponentStatus.Passed: return classStr + 'bg-green-100 text-green-600';
            default: return classStr;
        }
    }
    
    getProgress() {
        if(this.form.get('actualFinishDate').value)
            return 100;

        var start = this.form.get('actualStartDate').value ?? this.form.get('startDate').value;
        var end = this.form.get('dueDate').value;

        if(!start || !end) return 0;

        var totalDays = TimeUtil.getDaysBetween(new Date(start), new Date(end));
        var currentDays = TimeUtil.getDaysBetween(new Date(start), new Date());

        return Math.max(0, (totalDays ? currentDays / totalDays : 0) * 100);
    }

    getProgressClass() {
        var value = this.getProgress();

        if (value <= 50)
            return 'progress-red';
        else if (value <= 75)
            return 'progress-orange';
        else
            return 'progress-green';
    }


    loadChosenManufactureProcess(columnDefinitionId: number) {
        if(!columnDefinitionId) return;

        var col = this.manufactureProcesses?.flatMap(x => x.processes).flatMap(x => x.columnDefinitions).find(x => x.manufactureProcessColumnDefinitionId == columnDefinitionId);

        if(!col) return;

        //fetch the process
        this.manufactureProcessService.apiManufactureProcessGetProcessGet$Json$Response({processId: col.processId}).subscribe(res => {
            if(!res?.body?.data) return;

            var process = res.body.data;
            process.columnDefinitions = null;

            //get steps specifically for the column chosen
            for(var stage of process.stages)
                for(var step of stage.steps) {
                    step['value'] = step.values.find(x => x.columnDefinitionId == col.manufactureProcessColumnDefinitionId)?.value;
                    step.values = null;
                }

            console.log("process", process);
            this.selectedStageId = null;
            this.process = process;

            //if WO has a current stage, set it
            if(this.workOrder.currentStageId)
                this.selectCurrentStage(<any>{value: this.workOrder.currentStageId});
        });
    }

    selectCurrentStage(event: MatSelectChange) {
        this.selectedStageId = event.value;
        this.timerSeconds = this.workOrder.processStageInfos?.find(x =>
            x.processStageId == this.selectedStageId &&
            x.workOrderSwimlane == this.workOrder.workOrderSwimlane && x.resourceSwimLaneId == this.workOrder.resourceSwimLaneId)?.timerSeconds ?? 0;
    }

    getStageTotal = (stage: ManufactureProcessStageDto) => sumList(stage.steps.map(x => +x['value']));

    getExpectedStageValue = (stageId: number) =>
        this.process?.hasStages ? this.getStageTotal(this.process.stages.find(x => x.manufactureProcessStageId == stageId)) :
        (this.process?.stages?.flatMap(x => x.steps).find(x => x.manufactureProcessStepId == stageId) ?? {})['value'] ?? 0;

    getLoggedStageValue = (stageId: number) => (this.workOrder?.processStageInfos.find(x =>
        x.processStageId == stageId
        && x.workOrderSwimlane == this.workOrder.workOrderSwimlane && x.resourceSwimLaneId == this.workOrder.resourceSwimLaneId)?.timerSeconds ?? 0) / 60;

    openLinkedPo() {
        this.dialog.open(PurchaseOrderBuilderComponent, {
            data: {
                purchaseOrderId: this.workOrder?.salesOrderId,
                IsSupplier: false,
                IsProduct: true,
            },
            autoFocus: false,
        });
    }
    
    openBomDetails() {
        if (!this.bom) return;

        const fullBom = this.bom;
        const creatorName = this.GetUserName(fullBom.creatorUserId);
        const approverName = fullBom.approverUserId ? this.GetUserName(fullBom.approverUserId) : null;

        this.dialog.open(BomDetailsPopupComponent, {
            panelClass: 'base-popup-dialog',
            width: '1200px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            disableClose: false,
            autoFocus: false,
            data: {
                bom: fullBom,
                bomAssemblyProcesses: (fullBom as any).assemblyProcesses || [],
                creatorName: creatorName,
                approverName: approverName
            }
        });
    }

    markComponentReceived(bomItem: BomItemDto) {
        var componentInfo = this.getComponentInfoForBomItem(bomItem);
        componentInfo.status = WorkOrderComponentStatus.Received;
        componentInfo.receivedDate = new Date().toISOString();
    }

    inspectComponent(bomItem: BomItemDto) {
        var isPass = true;

        var dialog = this.dialog.open(AddItemDialogComponent, {
            data: {
                title: `Component Inspection`,
                items: [
                    {name: 'inspection notes', propertyName: 'inspectionFailNotes', initialValue: '', type: 'textarea' },
                ],
                buttons: [
                    {
                        name: 'Fail Inspection',
                        closeDialog: true,
                        closeWithFormValues: true,
                        color: 'warn',
                        isOnRight: true,
                        callback: () => {
                            isPass = false;
                        },
                    }
                ],
                overrideSaveText: 'Pass Inspection',
                overrideSaveClass: 'bg-green-600',
                additionalPreHtml: `
                <div>
                    <div>Component</div>
                    <div class="text-lg">${this.lookupPartById(bomItem.partId)?.number}</div>
                    <div class="text-sm">${this.lookupPartById(bomItem.partId)?.description}</div>
                </div>
                `,
                additionalHtml: `
                <div class="mb-6 -mt-4 text-md">Required if component fails inspection</div>
                <div class="bg-[#fffbeb] p-4 text-md text-[#92400e] rounded-md">
                    <div>Inspection Checklist:</div>
                    <ul class="mt-2 ml-4 list-disc list-outside">
                        <li>Verify part numbers match order</li>
                        <li>Check for physical damage</li>
                        <li>Confirm quantity received</li>
                        <li>Validate quality standards</li>
                    </ul>
                </div>
                `,
                overrideWidth: 40,
            }
        });
        
        dialog.afterClosed().subscribe((dialogRes) => {
            if (!dialogRes) return;

            var componentInfo = this.getComponentInfoForBomItem(bomItem);

            componentInfo.inspectionFailNotes = dialogRes.inspectionFailNotes;
            componentInfo.status = isPass ? WorkOrderComponentStatus.Passed : WorkOrderComponentStatus.Failed;
            componentInfo.inspectionDate = new Date().toISOString();
            componentInfo.inspectedByUserId = this.appAuth.getUser()?.id;
        });
    }

    GetUserName(userId: number) {
        if(!this.allUsers) return '';

        for(var u of this.allUsers)
        if(u.id == userId) return `${u.firstName} ${u.lastName}`;

        return '';
    }

    getMinutesText = (mins, dp) => TimeUtil.getHoursText(mins / 60, dp);
    getSecondsText = (seconds) => TimeUtil.getHoursText(seconds / 60 / 60);

    getTotalTimeLogged() {
        var times = this.workOrder?.stageInfos?.map(x => x.timerSeconds);
        if(!times.length) return '0h';

        return TimeUtil.getHoursText(sumList(times) / 60 / 60);
    }

    getCurrentStageName() {
        if(!this.selectedStageId || !this.process) return '';

        for(var p of this.process.stages) {
            if(this.process.hasStages) {
                if(p.manufactureProcessStageId == this.selectedStageId)
                    return p.name;
            }
            else {
                for(var s of p.steps)
                    if(s.manufactureProcessStepId == this.selectedStageId)
                        return s.name;
            }
        }
        return '';
    }

    buildModel() {
        var formValue = this.form.value as WorkOrderDto;
        console.log("form value", formValue);

        this.workOrder = Object.assign({}, this.workOrder ?? {}, formValue);

        this.workOrder.currentStageId = this.selectedStageId ?? null;
        this.workOrder.currentStageName = this.getCurrentStageName();

        this.workOrder.startDate = TimeUtil.setTo1amUTC(new Date(this.workOrder.startDate)).toISOString();
        this.workOrder.dueDate = TimeUtil.setTo2359UTC(new Date(this.workOrder.dueDate)).toISOString();

        console.log("work order", this.workOrder);

        if(this.currentStageInfo) {
            this.currentStageInfo.timerSeconds = this.stageTimerSeconds;
            this.currentStageInfo.assignedUserId = this.currentStageAssignedUserId ?? this.appAuth.getUser()?.id;
        }
        else if (this.workOrder) {
            this.currentStageInfo = {
                dateMovedToStage: new Date().toISOString(),
                timerSeconds: this.stageTimerSeconds,
                assignedUserId: this.currentStageAssignedUserId ?? this.appAuth.getUser()?.id,
                workOrderId: this.workOrderId,
                resourceSwimLaneId: this.workOrder.resourceSwimLaneId,
                workOrderSwimlane: this.workOrder.workOrderSwimlane,
            };
            this.workOrder.stageInfos.push(this.currentStageInfo);
        }
    }

    save(close: boolean = true) {
        this.buildModel();

        // Check if due date changed — compare by YYYY-MM-DD only (ignore time/format/timezone differences)
        var originalDueDate = this._originalDueDate;
        var currentEffectiveDate = this.workOrder?.revisedDueDate ?? originalDueDate;
        var newDate = this.workOrder?.dueDate ?? null;
        var dateChanged = newDate && currentEffectiveDate && newDate.substring(0, 10) !== currentEffectiveDate.substring(0, 10);

        if (dateChanged) {
            var dialog = this.dialog.open(AddItemDialogComponent, {
                autoFocus: true,
                data: {
                    title: 'Reason for Due Date Change',
                    items: [
                        { name: 'reason', type: 'textarea', mandatory: true, initialValue: '' },
                    ],
                    overrideSaveText: 'Confirm',
                    overrideSaveClass: 'bg-primary',
                }
            });

            dialog.afterClosed().subscribe(res => {
                if (!res?.reason?.trim()) return;

                this.workOrder.reasonForDateChange = res.reason.trim();
                // Store new date as revisedDueDate, restore original dueDate
                this.workOrder.revisedDueDate = this.workOrder.dueDate;
                this.workOrder.dueDate = originalDueDate;
                this.saveWorkOrderApi(close);
            });
            return;
        }

        // No date change — ensure dueDate stays as original
        this.workOrder.dueDate = originalDueDate;
        this.saveWorkOrderApi(close);
    }

    getPhaseTotalLoggedTime() {
        return sumList(this.workOrder?.processStageInfos?.filter(x =>
            x.workOrderSwimlane == this.currentStageInfo.workOrderSwimlane && x.resourceSwimLaneId == this.currentStageInfo.resourceSwimLaneId
        ).map(x => x.timerSeconds) ?? []);
    }

    getStagesForPhase = (phase: WorkOrderStageInfoDto) => this.workOrder?.processStageInfos.filter(x => x.workOrderSwimlane == phase.workOrderSwimlane && x.resourceSwimLaneId == phase.resourceSwimLaneId);

    getStageName(processStage: WorkOrderProcessStageInfoDto) {
        if(this.process?.hasStages)
            return this.process?.stages?.find(x => x.manufactureProcessStageId == processStage.processStageId)?.name ?? '';
        else
            return this.process?.stages?.flatMap(x => x.steps).find(x => x.manufactureProcessStepId == processStage.processStageId)?.name ?? '';
    }

    logTimeToProcessStage() {
        if(!this.selectedStageId) return;

        var dialog = this.dialog.open(AddItemDialogComponent, {
            data: {
                title: `Confirm or Adjust Time Spent`,
                items: [
                    {name: 'hours', initialValue: Math.floor(this.timerSeconds / 60 / 60), type: 'number', colSpan: 6, mandatory: true },
                    {name: 'minutes', initialValue: Math.floor(this.timerSeconds / 60), type: 'number', colSpan: 6, mandatory: true },
                ],
            }
        });
        
        dialog.afterClosed().subscribe((dialogRes) => {
            if (!dialogRes) return;

            dialogRes.hours ||= 0;
            dialogRes.minutes ||= 0;

            this.timerSeconds = dialogRes.hours * 60 * 60 + dialogRes.minutes * 60;

            
            var processStage = this.workOrder?.processStageInfos.find(x =>
                x.processStageId == this.selectedStageId &&
                x.workOrderSwimlane == this.workOrder.workOrderSwimlane && x.resourceSwimLaneId == this.workOrder.resourceSwimLaneId);

            if(processStage) {
                processStage.timerSeconds = this.timerSeconds;
                processStage.timeLoggedDate = new Date().toISOString();
                processStage.loggedUserId = this.appAuth.getUser()?.id;
            }
            else {
                this.workOrder.processStageInfos.push({
                    workOrderId: this.workOrderId,
                    processStageId: this.selectedStageId,
                    resourceSwimLaneId: this.workOrder.resourceSwimLaneId,
                    workOrderSwimlane: this.workOrder.workOrderSwimlane,
                    timerSeconds: this.timerSeconds,
                    loggedUserId: this.appAuth.getUser()?.id,
                    timeLoggedDate: new Date().toISOString(),
                })
            }

            //move on a stage/step if any left
            var getId = (x: ManufactureProcessStageDto | ManufactureProcessStepDto) => x['manufactureProcessStageId'] || x['manufactureProcessStepId'];
            var stagesOrSteps = this.process.hasStages ? this.process.stages : this.process.stages.flatMap(x => x.steps);
            var currentI = stagesOrSteps.findIndex(x => getId(x) == this.selectedStageId);
            if(currentI < (stagesOrSteps.length - 1))
                this.selectCurrentStage(<any>{value: getId(stagesOrSteps[currentI + 1])});
        });
    }

    proceedToNextPhase() {
        //get total logged time
        var totalTimeSeconds = this.getPhaseTotalLoggedTime();

        var dialog = this.dialog.open(AddItemDialogComponent, {
            data: {
                title: `Confirm or Adjust Time Spent`,
                items: [
                    {name: 'hours', initialValue: Math.floor(totalTimeSeconds / 60 / 60), type: 'number', colSpan: 6, mandatory: true },
                    {name: 'minutes', initialValue: Math.floor(totalTimeSeconds / 60) % 60, type: 'number', colSpan: 6, mandatory: true },
                ],
            }
        });
        
        dialog.afterClosed().subscribe((dialogRes) => {
            if (!dialogRes) return;

            dialogRes.hours ||= 0;
            dialogRes.minutes ||= 0;

            // this.timerSeconds = dialogRes.hours * 60 * 60 + dialogRes.minutes * 60;
            this.stageTimerSeconds = dialogRes.hours * 60 * 60 + dialogRes.minutes * 60;
            
            this.buildModel();

            this.currentStageInfo.timeLoggedDate = new Date().toISOString();

            var isUnset = !this.workOrder.resourceSwimLaneId && this.workOrder.workOrderSwimlane == null;
            // var isInWoLane = this.workOrder.workOrderSwimlane != null;
            var isInBacklog = this.workOrder.workOrderSwimlane == WorkOrderSwimlane.Backlog;
            var IsIn3rdParty = this.workOrder.workOrderSwimlane == WorkOrderSwimlane.ThirdParty;
            var isInDispatched = this.workOrder.workOrderSwimlane == WorkOrderSwimlane.Dispatched;
            var isInReadyForDispatch = this.workOrder.workOrderSwimlane == WorkOrderSwimlane.ReadyForDispatch;
            var isInMachine = !!this.workOrder.resourceSwimLaneId;
            var hasMachineAssigned = !!this.workOrder.assignedMachineId;

            // Dispatch failsafe: if moving from ReadyForDispatch to Dispatched, check siblings
            if (isInReadyForDispatch) {
                this.workOrderService.apiWorkOrderAreAllSiblingsCompleteGet$Json$Response({workOrderId: this.workOrder.workOrderId}).subscribe(res => {
                    if (res?.body?.data === false) {
                        const confirmation = this._fuseConfirmationService.open({
                            title: 'Dispatch Warning',
                            message: 'Are you sure you want to dispatch? Not all work orders within this sales order are completed.',
                            actions: {
                                confirm: { label: 'Yes', color: 'primary' },
                                cancel: { label: 'Cancel' }
                            }
                        });
                        confirmation.afterClosed().subscribe(result => {
                            if (result === 'confirmed')
                                this.advancePhaseAndSave(isInBacklog, IsIn3rdParty, isUnset, isInMachine, isInDispatched, hasMachineAssigned);
                        });
                    } else {
                        this.advancePhaseAndSave(isInBacklog, IsIn3rdParty, isUnset, isInMachine, isInDispatched, hasMachineAssigned);
                    }
                });
            } else {
                this.advancePhaseAndSave(isInBacklog, IsIn3rdParty, isUnset, isInMachine, isInDispatched, hasMachineAssigned);
            }
        });
    }

    private advancePhaseAndSave(isInBacklog: boolean, IsIn3rdParty: boolean, isUnset: boolean, isInMachine: boolean, isInDispatched: boolean, hasMachineAssigned: boolean) {
        //set next phase
        if((isInBacklog || IsIn3rdParty || isUnset) && hasMachineAssigned) {
            //is in backlog/unassigned and has assigned machine - move to that machine
            this.workOrder.resourceSwimLaneId = this.workOrder.assignedMachineId;
            this.workOrder.workOrderSwimlane = null;
        }
        else if(isInMachine) {
            //if machine lane, set to awaiting collection
            this.workOrder.resourceSwimLaneId = null;
            this.workOrder.workOrderSwimlane = WorkOrderSwimlane.AwaitingCollection;
        }
        else if (isInDispatched) {
            //if in final swimlane, just null the resource id - cannot advance anymore
            this.workOrder.resourceSwimLaneId = null;
        }
        else {
            //otherwise is in other wo lane - increment the work order swimlane
            this.workOrder.resourceSwimLaneId = null;
            this.workOrder.workOrderSwimlane++;
        }

        this.saveWorkOrderApi(true);
    }

    // markAsDispatched() {
    //     var tomorrow = TimeUtil.addDays(new Date(), 1);
    //     this.form.controls['dispatchDate'].setValue(tomorrow);
    //     this.proceedToNextPhase();
    // }


    createWorkOrderPurchaseOrders() {
        // var supplierId = this.form?.controls['supplierId'].value;
        if(!this.workOrderId || !this.bom)
            return;

        console.log("generating purchase orders...");

        var purchaseOrders: PurchaseOrderDto[] = [];
        var noSupplierParts = [];

        for(var bomItem of this.bom.items.filter(x => this.getPartCurrentStock(x.partId) < this.getBomItemRequiredAmount(x))) {
            var part = this.lookupPartById(bomItem.partId);

            if(!part) continue;
            
            var supplier = this.suppliers.find(x => x.supplierId == (part.primarySupplierId || part.secondarySupplierId || null));

            if(!supplier) {
                noSupplierParts.push(part.number);
                continue;
            }

            var poItem = <PurchaseOrderPartItemDto>{
                description: part.number,
                partId: bomItem.partId,
                quantity: this.getBomItemRequiredAmount(bomItem) - this.getPartCurrentStock(bomItem.partId),
                price: part.cost,
                vat: VatOption.Zero,
            };

            var po = purchaseOrders.find(x => x.supplier == supplier.name);

            if(po)
                po.purchaseOrderPartItems.push(poItem)
            else
                purchaseOrders.push(<PurchaseOrderDto>{
                    contactId: supplier?.mainContact?.supplierContactId ?? null,
                    company: supplier?.name,
                    supplier: supplier?.name,
                    isSupplier: true,
                    isProduct: false,
                    title: `${supplier?.name} Purchase Order for ${this.workOrder.workOrderRef}`,
                    freightAmount: 0,
                    purchaseOrderHireItems: [],
                    purchaseOrderPartItems: [poItem],
                    purchaseOrderProductItems: [],
                    manufactureColumnDefinitionId: this.form.controls['manufactureColumnDefinitionId'].value ?? null,
                });
        }

        var hasPos = purchaseOrders.length > 0;
        var multiplePos = purchaseOrders.length > 1;

        var poMessage = `
            ${purchaseOrders.map((purchaseOrder, i) => `
                <br/><br/>
                ${multiplePos ? `<div class='text-lg'>Purchase Order #${i+1}</div>` : ''}
                <strong>Supplier:</strong> ${purchaseOrder.supplier}<br/>
                <strong>Items:</strong>
                <div class='w-max'>
                    ${purchaseOrder.purchaseOrderPartItems.map(x => `
                        <div class='grid grid-cols-2 gap-4'>
                            <div>${x.description}</div>
                            <div>Qty: ${x.quantity}</div>
                        </div>`
                    ).join('')}`).join('')}
                </div>`;

        if(noSupplierParts.length > 0) {
            var noSupplierMessage = `
                    The following parts don't have a supplier set and so a purchase order can't be generated for them:<br/><br/>
                    ${noSupplierParts.join('<br/>')}`;

            poMessage = `
                    <br/><br/>
                    Would you like to still generate the following purchase order${multiplePos ? 's' : ''}?
                    ${poMessage}`;

            const confirmation = this._infoDialogService.open({
                title  : 'Parts with no Supplier',
                message: `
                    <br/>
                    <div class="max-h-[70vh] overflow-y-scroll">
                        ${noSupplierMessage}
                        ${hasPos ? poMessage : ''}
                    </div>
                `,
                actions: {
                    confirm: {
                        label: hasPos ? 'Create' : 'OK',
                    },
                    cancel: {
                        show: hasPos,
                    }
                },
            });

            confirmation.afterClosed().subscribe((result) =>
            {
                if (result === 'confirmed')
                {
                    for(var purchaseOrder of purchaseOrders)
                        this.purchaseOrderService.apiPurchaseOrderAddPurchaseOrderPost$Json$Response({body: purchaseOrder}).subscribe();
                }
            });

            return;
        }
        else if(!hasPos) {
            this._fuseConfirmationService.open({
                title  : 'No Purchase Orders Required',
                message: '<br/>All required parts from the BOM are in stock - no need to raise a purchase order.',
                actions: {
                    confirm: {
                        label: 'OK',
                    },
                    cancel: {
                        show: false,
                    }
                },
            });
            return;
        }
        
        const confirmation = this._infoDialogService.open({
            title  : `Create Purchase Order${multiplePos ? 's' : ''}`,
            message: `
                <br/>
                <div class="max-h-[70vh] overflow-y-scroll">
                    Are you sure you want to create ${multiplePos ? 'these' : 'this'} new purchase order${multiplePos ? 's' : ''}?
                    ${poMessage}
                </div>
            `,
            actions: {
                confirm: {
                    label: 'Create',
                },
            },
        });

        confirmation.afterClosed().subscribe((result) =>
        {
            if (result === 'confirmed')
            {
                for(var purchaseOrder of purchaseOrders)
                    this.purchaseOrderService.apiPurchaseOrderAddPurchaseOrderPost$Json$Response({body: purchaseOrder}).subscribe();
            }
        });
    }

    saveWorkOrderApi(close: boolean = true) {
        var user = this.appAuth.getUser();
        var userName = `${user?.firstName} ${user?.lastName}`.trim();
        if(this.workOrderId)
            this.workOrderService.apiWorkOrderUpdateWorkOrderPost$Json$Response({body: this.workOrder, userName: userName}).subscribe(res => {
                if(!res?.body?.data) return;

                if(close)
                    this.close();
            });
        else
            this.workOrderService.apiWorkOrderAddWorkOrderPost$Json$Response({body: this.workOrder}).subscribe(res => {
                if(!res?.body?.data) return;

                if(close)
                    this.close();
            });
    }

    close() {
        this.dialogRef.close(null);
    }

    addComment(text: string) {
        if (!text?.trim() || !this.workOrderId) return;

        var user = this.appAuth.getUser();
        this.workOrderService.apiWorkOrderAddCommentPost$Json$Response({
            body: {
                workOrderId: this.workOrderId,
                text: text.trim(),
                userId: user?.id,
            }
        }).subscribe(res => {
            if (!res?.body?.data) return;
            this.workOrder.workOrderComments = [res.body.data, ...(this.workOrder.workOrderComments ?? [])];
            this.cdr.detectChanges();
        });
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }


    // Files
    loadFilesForProduct() {
        if(!this.workOrder.productId) return;

        this.dlmProductService.apiDlmProductGetFilesGet$Json({ productId: this.workOrder.productId }).subscribe(res => {
            if(!res?.data) return;

            this.files = res.data;

            this.cdr.detectChanges();
        });
    }

    async openFileUpload(){
        var dialog = this.dialog.open(UploadFilePopupComponent, {autoFocus: false});

        dialog.afterClosed().subscribe((dialogRes) => {
        if (!dialogRes) return;

        this.uploadImages(dialogRes);
        });    
    }

    deleteFile(file: DlmProductFileDto, event: any){
        event.stopPropagation();

        const confirmation = this._fuseConfirmationService.open({
        title  : "Delete File",
        message: 'Are you sure you want to delete this file?',
        actions: {
            confirm: {
                label: "Delete",
            },
        },
        });
    
        confirmation.afterClosed().subscribe((result) => {
        if ( result === 'confirmed' )
        {
            this.dlmProductService.apiDlmProductDeleteFileFileIdDelete$Json({ fileId: file?.dlmProductFileId }).subscribe((res) => {
                if(!res) return;
    
                this.loadFilesForProduct();
            });
        }
        });
    }

    async uploadImages(files: FileList) {
    if (!this.workOrder.productId) return;
    
    var newFiles = Array.from(files);

    newFiles.forEach((file) => {
        var imageFormat = file.name!.split(".").pop()?.trim()?.toLowerCase();
        var blob: Blob = file;
        var name: string = file.name;

        this.dlmProductService.apiDlmProductUploadFileProductIdPost$Json({ 
            productId: this.workOrder.productId, 
            filename: name, 
            body: { formFile: blob },
        }).subscribe((res) => {
            this.loadFilesForProduct();
        });
        });
    }

    download(file: DlmProductFileDto){
        this.dlmProductService.apiDlmProductDownloadFileFileIdGet({ fileId: file.dlmProductFileId }).subscribe((res) => {
            if(!res) return;

            const blob = new Blob([res], { type: 'application/octet-stream' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            if(!file?.name.endsWith(file?.type))
                file.name += file?.type;

            link.download = file.name;
            link.click();
            window.URL.revokeObjectURL(url);
        });
    }

    
    printQrCode() {
        var printArea = document.getElementById('qr-print-area');
        if(!printArea || !this.workOrder?.workOrderRef) return;

        var qrSvg = printArea.querySelector('qrcode')?.innerHTML ?? '';

        var printWindow = window.open('', '_blank', 'width=300,height=200');
        printWindow.document.write(`
            <html><head><title>Label - ${this.workOrder.workOrderRef}</title>
            <style>
                @page { size: 37mm 25mm; margin: 0; }
                body { display: flex; flex-direction: column; align-items: center; justify-content: center; width: 37mm; height: 25mm; margin: 0; padding: 1mm; box-sizing: border-box; font-family: Arial, sans-serif; }
                .qr-container { text-align: center; }
                .qr-container svg { width: 18mm; height: 18mm; }
                .code-label { font-weight: bold; font-size: 6pt; margin-top: 0.5mm; }
            </style></head><body>
            <div class="qr-container">${qrSvg}</div>
            <div class="code-label">${this.workOrder.workOrderRef}</div>
            </body></html>
        `);
        printWindow.document.close();
        printWindow.onload = () => { printWindow.print(); printWindow.close(); };
    }
}
